import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase_client";
import { useSession } from "../../contexts/SessionContext";
import { generateEInvoiceFromPayload } from "../../services/invoiceService";
import { MALAYSIA_STATES } from "../EInvoiceDetailsModal";

export interface PartnerInvoice {
  id: string;
  invoice_number: string;
  description: string;
  amount: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  classification_code: string;
  buyer_legal_name: string;
  buyer_tin: string;
  buyer_id_type: string;
  buyer_brn: string;
  buyer_sst: string;
  buyer_email: string;
  buyer_phone: string;
  buyer_address_line_1: string;
  buyer_address_line_2: string;
  buyer_address_line_3: string;
  buyer_city: string;
  buyer_postal_code: string;
  buyer_state_code: string;
  lhdn_status: string | null;
  lhdn_submission_uid: string | null;
  lhdn_document_uuid: string | null;
  lhdn_long_id: string | null;
  lhdn_submitted_at: string | null;
  lhdn_validated_at: string | null;
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  valid: { cls: "bg-green-100 text-green-700", label: "Valid" },
  submitted: { cls: "bg-blue-100 text-blue-700", label: "Pending" },
  invalid: { cls: "bg-red-100 text-red-700", label: "Invalid" },
  cancelled: { cls: "bg-gray-200 text-gray-500", label: "Cancelled" },
  rejected: { cls: "bg-orange-100 text-orange-700", label: "Rejected" },
};

function stateName(code: string) {
  return MALAYSIA_STATES.find((s) => s.Code === code)?.State ?? code;
}

function formatMoney(amount: number, currency: string) {
  return `${currency} ${Number(amount).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const PARTNER_BUYER = {
  org_id: "74be9d9d-6130-4ee6-96be-526f2101a32a",
  buyer_legal_name: "AUDIT ONLINE SOLUTION SDN. BHD.",
  buyer_tin: "C23600547020",
  buyer_id_type: "BRN",
  buyer_brn: "201101002832",
  buyer_sst: "J31-2003-32000012",
  buyer_email: "admin@auditplus.ai",
  buyer_phone: "+60167157433",
  buyer_address_line_1: "No. 79-02, Jalan Mutiara Emas 10/2",
  buyer_address_line_2: "Taman Mount Austin",
  buyer_address_line_3: "",
  buyer_city: "Johor Bahru",
  buyer_postal_code: "81100",
  buyer_state_code: "01",
};

type MonthPoint = { year: number; month: number };

function monthIndex(point: MonthPoint) {
  return point.year * 12 + point.month;
}

function formatMonthLong(point: MonthPoint) {
  return `${MONTHS_LONG[point.month]} ${point.year}`;
}

function monthSpan(start: MonthPoint, end: MonthPoint) {
  return monthIndex(end) - monthIndex(start) + 1;
}

function toIsoDate(point: MonthPoint, endOfMonth: boolean) {
  const month = String(point.month + 1).padStart(2, "0");
  if (!endOfMonth) return `${point.year}-${month}-01`;
  const lastDay = new Date(point.year, point.month + 1, 0).getDate();
  return `${point.year}-${month}-${String(lastDay).padStart(2, "0")}`;
}

function buildDescription(start: MonthPoint, end: MonthPoint, amount: number) {
  const months = monthSpan(start, end);
  const range = `${formatMonthLong(start)} to ${formatMonthLong(end)}`;
  const perMonthCents = Math.round((amount / months) * 100);
  const evenSplit = perMonthCents * months === Math.round(amount * 100);
  const splitText = evenSplit
    ? ` (RM ${(perMonthCents / 100).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} x ${months} month${months === 1 ? "" : "s"})`
    : "";
  return `Shared rental, ${range}${splitText}.`;
}

function rangesOverlap(aStart: string, aEnd: string, bStart: string | null, bEnd: string | null) {
  if (!bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
}

export default function PartnerEInvoiceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useSession();
  const [rows, setRows] = useState<PartnerInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState(2026);
  const [rangeStart, setRangeStart] = useState<MonthPoint | null>(null);
  const [rangeEnd, setRangeEnd] = useState<MonthPoint | null>(null);
  const [amountInput, setAmountInput] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("partner_einvoices")
        .select("id, invoice_number, description, amount, currency, period_start, period_end, classification_code, buyer_legal_name, buyer_tin, buyer_id_type, buyer_brn, buyer_sst, buyer_email, buyer_phone, buyer_address_line_1, buyer_address_line_2, buyer_address_line_3, buyer_city, buyer_postal_code, buyer_state_code, lhdn_status, lhdn_submission_uid, lhdn_document_uuid, lhdn_long_id, lhdn_submitted_at, lhdn_validated_at")
        .order("created_at", { ascending: false });
      if (error) {
        console.error("Failed to load partner invoices", error);
        return;
      }
      setRows((data ?? []) as PartnerInvoice[]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const issue = async (row: PartnerInvoice) => {
    setActingId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("submit-partner-einvoice", {
        body: { partner_invoice_id: row.id, action: "submit" },
      });
      if (error || data?.ok === false) {
        const detail = data?.error ? JSON.stringify(data.error) : error?.message || "Unknown error";
        alert(`Failed to issue ${row.invoice_number}: ${detail}`);
      }
      setConfirmId(null);
      await load();
    } catch (err) {
      alert("Failed to issue partner e-invoice. " + String(err));
    } finally {
      setActingId(null);
    }
  };

  const checkStatus = async (row: PartnerInvoice) => {
    setActingId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("submit-partner-einvoice", {
        body: { partner_invoice_id: row.id, action: "check" },
      });
      if (error) {
        alert("Status check failed: " + error.message);
        return;
      }
      if (data?.error) {
        alert("Status check failed: " + (typeof data.error === "string" ? data.error : JSON.stringify(data.error)));
      }
      await load();
    } finally {
      setActingId(null);
    }
  };

  const downloadPdf = async (row: PartnerInvoice) => {
    if (row.lhdn_status !== "valid" || !row.lhdn_document_uuid || !row.lhdn_long_id) {
      alert("Partner e-invoice is not yet validated. No PDF available.");
      return;
    }
    setActingId(row.id);
    try {
      const { data, error } = await supabase
        .from("partner_einvoices")
        .select("lhdn_payload, lhdn_submitted_at")
        .eq("id", row.id)
        .single();
      if (error || !data?.lhdn_payload) {
        alert("Payload not found for this invoice.");
        return;
      }
      const pdfBlob = await generateEInvoiceFromPayload(
        data.lhdn_payload as Record<string, unknown>,
        row.lhdn_document_uuid,
        row.lhdn_long_id,
        data.lhdn_submitted_at
      );
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${row.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF.");
    } finally {
      setActingId(null);
    }
  };

  const amount = Number(amountInput);
  const selection = rangeStart && rangeEnd ? { start: rangeStart, end: rangeEnd } : null;
  const selectedMonths = selection ? monthSpan(selection.start, selection.end) : 0;
  const periodStart = selection ? toIsoDate(selection.start, false) : "";
  const periodEnd = selection ? toIsoDate(selection.end, true) : "";
  const descriptionPreview = selection && amount > 0 ? buildDescription(selection.start, selection.end, amount) : "";
  const overlapping = selection
    ? rows.filter((row) => rangesOverlap(periodStart, periodEnd, row.period_start, row.period_end))
    : [];

  const onMonthClick = (month: number) => {
    const point = { year: viewYear, month };
    if (!rangeStart || rangeEnd) {
      setRangeStart(point);
      setRangeEnd(null);
      return;
    }
    if (monthIndex(point) < monthIndex(rangeStart)) {
      setRangeEnd(rangeStart);
      setRangeStart(point);
      return;
    }
    setRangeEnd(point);
  };

  const createInvoice = async () => {
    if (!selection || !(amount > 0)) return;
    const { start, end } = selection;
    setCreating(true);
    try {
      const used = rows
        .map((row) => Number(row.invoice_number.replace(/\D/g, "")))
        .filter((n) => Number.isFinite(n));
      const next = (used.length ? Math.max(...used) : 0) + 1;
      const invoiceNumber = `RENT${String(next).padStart(6, "0")}`;
      const { error } = await supabase.from("partner_einvoices").insert({
        invoice_number: invoiceNumber,
        description: buildDescription(start, end, amount),
        amount,
        currency: "MYR",
        period_start: periodStart,
        period_end: periodEnd,
        classification_code: "030",
        created_by: user?.id ?? null,
        ...PARTNER_BUYER,
      });
      if (error) {
        alert("Could not create the partner invoice: " + error.message);
        return;
      }
      setAmountInput("");
      await load();
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Partner e-Invoice</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Pick the months, type the total, then issue. Supplier stays Smart Filing. These stay out of consolidated billing.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => load()} className="text-gray-400 hover:text-gray-600" title="Refresh">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-800">New invoice</div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <button type="button" onClick={() => setViewYear((y) => y - 1)} className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">‹</button>
                <span className="w-12 text-center font-medium">{viewYear}</span>
                <button type="button" onClick={() => setViewYear((y) => y + 1)} className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">›</button>
              </div>
            </div>
            <p className="text-xs text-gray-500">Click the first month, then the last month.</p>
            <div className="grid grid-cols-6 gap-2">
              {MONTHS.map((label, month) => {
                const point = { year: viewYear, month };
                const index = monthIndex(point);
                const inRange = !!selection && index >= monthIndex(selection.start) && index <= monthIndex(selection.end);
                const isEdge = (rangeStart && index === monthIndex(rangeStart)) || (rangeEnd && index === monthIndex(rangeEnd));
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => onMonthClick(month)}
                    className={`py-2 text-xs rounded-md border ${
                      isEdge
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : inRange
                          ? "bg-indigo-100 text-indigo-800 border-indigo-200"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-gray-600">
                Total amount (MYR)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 block w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </label>
              <button
                type="button"
                onClick={createInvoice}
                disabled={creating || !selection || !(amount > 0)}
                className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? "Saving…" : "Create invoice"}
              </button>
            </div>
            {selection && (
              <p className="text-xs text-gray-600">
                {formatMonthLong(selection.start)} to {formatMonthLong(selection.end)} · {selectedMonths} month{selectedMonths === 1 ? "" : "s"}
                {amount > 0 ? ` · ${formatMoney(amount, "MYR")}` : ""}
              </p>
            )}
            {descriptionPreview && <p className="text-xs text-gray-500">{descriptionPreview}</p>}
            {overlapping.length > 0 && (
              <p className="text-xs text-amber-700">
                This range overlaps {overlapping.map((row) => row.invoice_number).join(", ")}. You can still create another invoice if the amount is a separate charge.
              </p>
            )}
          </div>
          {loading ? (
            <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No partner invoices yet.</p>
          ) : (
            rows.map((row) => {
              const badge = row.lhdn_status
                ? STATUS_BADGE[row.lhdn_status] ?? { cls: "bg-gray-100 text-gray-500", label: row.lhdn_status }
                : { cls: "bg-gray-100 text-gray-600", label: "Not issued" };
              const canIssue = !row.lhdn_status || row.lhdn_status === "invalid" || row.lhdn_status === "rejected";
              const canCheck = row.lhdn_status === "submitted" || (row.lhdn_status === "valid" && !row.lhdn_long_id);
              const busy = actingId === row.id;
              const address = [row.buyer_address_line_1, row.buyer_address_line_2, row.buyer_address_line_3, row.buyer_postal_code, row.buyer_city, stateName(row.buyer_state_code)]
                .filter(Boolean)
                .join(", ");
              return (
                <div key={row.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-sm font-medium text-gray-800">{row.invoice_number}</div>
                      <div className="text-sm text-gray-800 mt-1">{row.buyer_legal_name}</div>
                      <div className="text-sm font-medium text-gray-900 mt-1">{formatMoney(row.amount, row.currency)}</div>
                      {row.period_start && row.period_end && (
                        <div className="text-xs text-gray-500 mt-1">{row.period_start} to {row.period_end}</div>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                  </div>
                  <p className="text-xs text-gray-600">{row.description}</p>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <div><span className="text-gray-400">TIN</span> {row.buyer_tin}</div>
                    <div><span className="text-gray-400">{row.buyer_id_type}</span> {row.buyer_brn}</div>
                    <div><span className="text-gray-400">SST</span> {row.buyer_sst || "NA"}</div>
                    <div><span className="text-gray-400">Phone</span> {row.buyer_phone}</div>
                    <div className="col-span-2"><span className="text-gray-400">Email</span> {row.buyer_email}</div>
                    <div className="col-span-2"><span className="text-gray-400">Address</span> {address}</div>
                  </dl>
                  <div className="flex justify-end gap-2">
                    {canCheck && (
                      <button
                        onClick={() => checkStatus(row)}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {busy ? "Checking…" : "Check status"}
                      </button>
                    )}
                    {row.lhdn_status === "valid" && (
                      <button
                        onClick={() => downloadPdf(row)}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Download PDF
                      </button>
                    )}
                    {canIssue && confirmId !== row.id && (
                      <button
                        onClick={() => setConfirmId(row.id)}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Issue to LHDN
                      </button>
                    )}
                    {canIssue && confirmId === row.id && (
                      <>
                        <button
                          onClick={() => setConfirmId(null)}
                          disabled={busy}
                          className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => issue(row)}
                          disabled={busy}
                          className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {busy ? "Issuing…" : "Confirm & issue"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
